/**
 * Constantes da Aplicação
 *
 * Define valores constantes utilizados em toda a aplicação,
 * como labels, opções de select e configurações.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { SelectOption } from '@/types/common';
import { OrderStatus, OrderType } from '@/types/order';
import { ProductStatus } from '@/types/product';
import { TenantType, TenantStatus, TenantPlan } from '@/types/tenant';
import { UserRole, UserStatus } from '@/types/user';

/**
 * Labels de status de pedido
 */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'Aguardando Pagamento',
  CONFIRMED: 'Confirmado',
  PREPARING: 'Em Preparo',
  READY: 'Pronto',
  OUT_FOR_DELIVERY: 'Em Entrega',
  DELIVERED: 'Entregue',
  COMPLETED: 'Finalizado',
  CANCELLED: 'Cancelado',
};

/**
 * Cores de status de pedido
 */
export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'warning',
  CONFIRMED: 'info',
  PREPARING: 'info',
  READY: 'success',
  OUT_FOR_DELIVERY: 'info',
  DELIVERED: 'success',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

/**
 * Labels de tipo de pedido
 */
export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  DINE_IN: 'Consumo no Local',
  TAKEAWAY: 'Retirada',
  DELIVERY: 'Entrega',
};

/**
 * Labels de status de produto
 */
export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  OUT_OF_STOCK: 'Sem Estoque',
};

/**
 * Labels de tipo de estabelecimento
 */
export const TENANT_TYPE_LABELS: Record<TenantType, string> = {
  RESTAURANT: 'Restaurante',
  BAKERY: 'Padaria / Confeitaria',
  SNACK_BAR: 'Lanchonete',
  PIZZERIA: 'Pizzaria',
  BARBERSHOP: 'Barbearia',
  BAR: 'Bar',
  CAFE: 'Cafeteria',
  OTHER: 'Outro',
};

/**
 * Opções de tipo de estabelecimento para select
 */
export const TENANT_TYPE_OPTIONS: SelectOption<TenantType>[] = [
  { value: 'RESTAURANT', label: 'Restaurante' },
  { value: 'BAKERY', label: 'Padaria / Confeitaria' },
  { value: 'SNACK_BAR', label: 'Lanchonete' },
  { value: 'PIZZERIA', label: 'Pizzaria' },
  { value: 'BARBERSHOP', label: 'Barbearia' },
  { value: 'BAR', label: 'Bar' },
  { value: 'CAFE', label: 'Cafeteria' },
  { value: 'OTHER', label: 'Outro' },
];

/**
 * Labels de status de tenant
 */
export const TENANT_STATUS_LABELS: Record<TenantStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  TRIAL: 'Em teste',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
};

/**
 * Labels de plano
 */
export const TENANT_PLAN_LABELS: Record<TenantPlan, string> = {
  FREE: 'Gratuito',
  BASIC: 'Básico',
  PRO: 'Profissional',
  ENTERPRISE: 'Empresarial',
};

/**
 * Labels de role de usuário
 */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super Administrador',
  OWNER: 'Proprietário',
  ADMIN: 'Administrador',
  MANAGER: 'Gerente',
  CASHIER: 'Caixa',
  WAITER: 'Garçom',
  KITCHEN: 'Cozinha',
  DELIVERY: 'Entregador',
};

/**
 * Opções de role de usuário para select
 */
export const USER_ROLE_OPTIONS: SelectOption<UserRole>[] = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'MANAGER', label: 'Gerente' },
  { value: 'CASHIER', label: 'Caixa' },
  { value: 'WAITER', label: 'Garçom' },
  { value: 'KITCHEN', label: 'Cozinha' },
  { value: 'DELIVERY', label: 'Entregador' },
];

/**
 * Labels de status de usuário
 */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  PENDING: 'Pendente',
  BLOCKED: 'Bloqueado',
};

/**
 * Labels de forma de pagamento
 */
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Dinheiro',
  CREDIT_CARD: 'Cartão de Crédito',
  DEBIT_CARD: 'Cartão de Débito',
  PIX: 'PIX',
  VOUCHER: 'Vale Refeição',
};

/**
 * Labels de status de pagamento
 */
export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  AUTHORIZED: 'Pré-autorizado',
  PAID: 'Pago',
  FAILED: 'Falhou',
  REFUNDED: 'Estornado',
  CANCELLED: 'Cancelado',
};

/**
 * Labels de canal de venda
 */
export const SALES_CHANNEL_LABELS: Record<string, string> = {
  WEB: 'Site',
  APP: 'Aplicativo',
  PWA: 'PWA',
  WHATSAPP: 'WhatsApp',
  QRCODE: 'QR Code',
  PHONE: 'Telefone',
  COUNTER: 'Balcão',
};

/**
 * Dias da semana
 */
export const WEEKDAYS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

/**
 * Dias da semana abreviados
 */
export const WEEKDAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/**
 * Meses do ano
 */
export const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/**
 * Meses abreviados
 */
export const MONTHS_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

/**
 * Opcoes de período para filtro de data
 */
export const DATE_RANGE_OPTIONS = [
  { value: '', label: 'Todos os períodos' },
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'week', label: 'Esta semana' },
  { value: 'month', label: 'Este mês' },
  { value: 'custom', label: 'Personalizado' },
];

/**
 * Opções de paginação
 */
export const PAGINATION_OPTIONS = [10, 20, 50, 100];

/**
 * Limite padrão de paginação
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Estados brasileiros
 */
export const BRAZILIAN_STATES: SelectOption[] = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];
